import { profile } from "../store.js";

const KG_PER_LB = 0.45359237;

export function unit() {
  return profile()?.weight_unit || "kg";
}

export function kgToLbs(kg) {
  return kg / KG_PER_LB;
}
export function lbsToKg(lbs) {
  return lbs * KG_PER_LB;
}

/** Convert a stored kg value to the user's display unit, rounded sensibly. */
export function toDisplayWeight(kg) {
  if (kg == null || kg === "") return "";
  const u = unit();
  const value = u === "lbs" ? kgToLbs(kg) : kg;
  return roundWeight(value, u);
}

/** Convert a user-entered display value back to kg for storage. */
export function toStorageKg(displayValue) {
  if (displayValue == null || displayValue === "") return null;
  const n = parseFloat(displayValue);
  if (isNaN(n)) return null;
  return unit() === "lbs" ? lbsToKg(n) : n;
}

export function roundWeight(value, u = unit()) {
  const step = u === "lbs" ? 1 : 0.05;
  const rounded = Math.round(value / step) * step;
  return Math.round(rounded * 100) / 100;
}

export function weightLabel() {
  return unit();
}

export function fmtWeight(kg) {
  if (kg == null) return "—";
  return `${toDisplayWeight(kg)} ${unit()}`;
}

/** Volume (kg·reps) converted to display unit. */
export function fmtVolume(volumeKg) {
  if (!volumeKg) return `0 ${unit()}`;
  const v = unit() === "lbs" ? kgToLbs(volumeKg) : volumeKg;
  return `${Math.round(v).toLocaleString()} ${unit()}`;
}

export function fmtDuration(seconds) {
  if (!seconds) return "0:00";
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  if (h > 0) return `${h}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
  return `${m}:${String(s).padStart(2, "0")}`;
}

export function fmtDurationLong(seconds) {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

// Treat naive SQLite timestamps (UTC, no Z) as UTC.
export function parseDate(ts) {
  if (!ts) return null;
  return new Date(ts.includes("Z") || ts.includes("T") ? ts : ts.replace(" ", "T") + "Z");
}

export function fmtDate(ts) {
  const d = parseDate(ts);
  if (!d) return "";
  return d.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });
}

export function fmtDateTime(ts) {
  const d = parseDate(ts);
  if (!d) return "";
  return d.toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
}

export function relativeDay(ts) {
  const d = parseDate(ts);
  if (!d) return "";
  const days = Math.floor((Date.now() - d.getTime()) / 86400000);
  if (days <= 0) return "Today";
  if (days === 1) return "Yesterday";
  if (days < 7) return `${days} days ago`;
  if (days < 14) return "Last week";
  if (days < 60) return `${Math.floor(days / 7)} weeks ago`;
  return `${Math.floor(days / 30)} months ago`;
}

// Reps capped at 10 — Epley overestimates past ~10 reps (must match server/lib/strength.ts).
export function epley1RM(weightKg, reps) {
  return reps > 0 ? weightKg * (1 + Math.min(reps, 10) / 30) : 0;
}

// ── Typed-metric helpers (duration / distance / pace) ────────────────────────
const M_PER_MI = 1609.344;

/** Distance unit follows the weight unit: metric users get km, imperial get miles. */
export function distanceUnit() {
  return unit() === "lbs" ? "mi" : "km";
}

/** Stored metres → display number in the user's distance unit (2 dp). */
export function toDisplayDistance(m) {
  if (m == null || m === "") return "";
  const v = distanceUnit() === "mi" ? m / M_PER_MI : m / 1000;
  return Math.round(v * 100) / 100;
}

/** User-entered distance (km/mi) → metres for storage. */
export function toStorageDistanceM(value) {
  if (value == null || value === "") return null;
  const n = parseFloat(value);
  if (isNaN(n)) return null;
  return distanceUnit() === "mi" ? n * M_PER_MI : n * 1000;
}

export function fmtDistance(m) {
  if (m == null) return "—";
  return `${toDisplayDistance(m)} ${distanceUnit()}`;
}

/** Pace (time per distance unit), e.g. "5:30/km". Empty when either side is missing. */
export function fmtPace(seconds, meters) {
  if (!seconds || !meters) return "";
  const perUnit = distanceUnit() === "mi" ? meters / M_PER_MI : meters / 1000;
  if (perUnit <= 0) return "";
  const paceSec = seconds / perUnit;
  const m = Math.floor(paceSec / 60);
  const s = Math.round(paceSec % 60);
  return `${m}:${String(s).padStart(2, "0")}/${distanceUnit()}`;
}

/** Parse a time input: "mm:ss" / "h:mm:ss" colon notation, or a bare number of seconds. */
export function parseTimeInput(str) {
  if (str == null) return null;
  const s = String(str).trim();
  if (!s) return null;
  if (s.includes(":")) {
    const parts = s.split(":").map((p) => parseInt(p, 10));
    if (parts.some((n) => isNaN(n))) return null;
    return parts.reduce((acc, p) => acc * 60 + p, 0);
  }
  const n = parseFloat(s);
  return isNaN(n) ? null : Math.round(n);
}

/** Stored seconds → "m:ss" for an input value; "" when null. */
export function fmtTimeShort(seconds) {
  if (seconds == null || seconds === "") return "";
  return fmtDuration(seconds);
}

// ── Length helpers (body measurements; cm stored, in displayed for imperial) ──
const CM_PER_IN = 2.54;

export function lengthUnit() {
  return unit() === "lbs" ? "in" : "cm";
}

export function toDisplayLength(cm) {
  if (cm == null || cm === "") return "";
  const v = lengthUnit() === "in" ? cm / CM_PER_IN : cm;
  return Math.round(v * 10) / 10;
}

export function toStorageCm(value) {
  if (value == null || value === "") return null;
  const n = parseFloat(value);
  if (isNaN(n)) return null;
  return lengthUnit() === "in" ? n * CM_PER_IN : n;
}

export function fmtLength(cm) {
  if (cm == null) return "—";
  return `${toDisplayLength(cm)} ${lengthUnit()}`;
}
